import { Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import AppButton from "../../../../components/ui/AppButton";
import AppInputField from "../../../../components/ui/AppInputField";

interface TripDetailsFormData {
  fromLocation: string;
  toLocation: string;
  approximateDistance: number;
  totalExpectedIncome: number;
  totalIncomeEarned: number;
  totalPassengers: number;
  perPassengerRate: number;
  driverId: string;
  vehicleId: string;
}

interface IncomeInput {
  description: string;
  expectedAmount: number;
  earnedAmount: number;
}

interface IncomeStepProps {
  control: Control<TripDetailsFormData>;
  incomes: IncomeInput[];
  errors: FieldErrors<TripDetailsFormData>;
  setIncomes: React.Dispatch<React.SetStateAction<IncomeInput[]>>;
  onSubmit: () => void;
  onBack: () => void;
}

const IncomeStep = ({
  control,
  incomes,
  errors,
  setIncomes,
  onSubmit,
  onBack,
}: IncomeStepProps) => {
  const handleAddIncome = () => {
    setIncomes([
      ...incomes,
      { description: "", expectedAmount: 0, earnedAmount: 0 },
    ]);
  };

  const handleRemoveIncome = (index: number) => {
    setIncomes(incomes.filter((_, i) => i !== index));
  };

  const handleUpdateIncome = (
    index: number,
    field: keyof IncomeInput,
    value: string | number,
  ) => {
    const updated = [...incomes];
    updated[index] = { ...updated[index], [field]: value };
    setIncomes(updated);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Controller
            control={control}
            name="totalIncomeEarned"
            render={({ field }) => (
              <AppInputField
                label="Total income"
                type="number"
                error={errors.totalIncomeEarned?.message}
                {...field}
                value={field.value || ""}
                onChange={(e) =>
                  field.onChange(parseFloat(e.target.value) || 0)
                }
                min={0}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                placeholder="Enter total income"
              />
            )}
          />

          <Controller
            control={control}
            name="totalExpectedIncome"
            render={({ field }) => (
              <AppInputField
                label="Total expected income"
                type="number"
                error={errors.totalExpectedIncome?.message}
                {...field}
                value={field.value || ""}
                onChange={(e) =>
                  field.onChange(parseFloat(e.target.value) || 0)
                }
                min={0}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                placeholder="Enter total expected income"
              />
            )}
          />

          <Controller
            control={control}
            name="totalPassengers"
            render={({ field }) => (
              <AppInputField
                label="Total passengers"
                type="number"
                error={errors.totalPassengers?.message}
                {...field}
                value={field.value || ""}
                onChange={(e) =>
                  field.onChange(parseFloat(e.target.value) || 0)
                }
                min={0}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                placeholder="Enter total passengers"
              />
            )}
          />

          <Controller
            control={control}
            name="perPassengerRate"
            render={({ field }) => (
              <AppInputField
                label="Rate per passengers"
                type="number"
                error={errors.perPassengerRate?.message}
                {...field}
                value={field.value || ""}
                onChange={(e) =>
                  field.onChange(parseFloat(e.target.value) || 0)
                }
                min={0}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                placeholder="Enter rate per passengers"
              />
            )}
          />
        </div>

        <h3 className="text-[13px] font-semibold">Trip Income</h3>

        <div className="border border-gray-100 rounded-[10px] p-4 items-center justify-center space-y-4">
          {incomes.map((income, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-4 w-full"
            >
              <AppInputField
                label={`Description ${index + 1}`}
                type="text"
                value={income.description}
                onChange={(e) =>
                  handleUpdateIncome(index, "description", e.target.value)
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter trip description"
              />
              <AppInputField
                label={`Amount ${index + 1}`}
                type="number"
                value={income.expectedAmount || ""}
                onChange={(e) =>
                  handleUpdateIncome(
                    index,
                    "expectedAmount",
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter expected income"
              />
              <AppInputField
                label={`Amount earned ${index + 1}`}
                type="number"
                value={income.earnedAmount || ""}
                onChange={(e) =>
                  handleUpdateIncome(
                    index,
                    "earnedAmount",
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter income earned"
              />

              <div className="w-full md:w-[48px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px] mt-0 md:mt-8">
                <button
                  type="button"
                  onClick={() => handleRemoveIncome(index)}
                  className="text-red-500 hover:text-red-600 transition-colors"
                  aria-label="Remove crew member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={handleAddIncome}
          className="text-secondary hover:text-primary transition-colors text-[13px] font-semibold"
        >
          + Add Income
        </button>
      </div>

      <div className="flex justify-start items-start space-x-4 pt-4">
        <AppButton
          type="button"
          onClick={onBack}
          className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px]"
        >
          Back
        </AppButton>

        <AppButton
          type="button"
          onClick={onSubmit}
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px]"
        >
          Submit
        </AppButton>
      </div>
    </div>
  );
};

export default IncomeStep;
